<template>
  <label
    ref="root"
    :class="{
      'slo-select': true,
      [`size-${size}`]: size,
      [`variant-${variant}`]: variant,
      'has-value': hasValue,
      disabled: disabled || fieldState.disabled,
      'focus-within': fieldState.focusWithin,
      'slo-select--error': error,
      'slo-select--tooltip': toolTip
    }"
    v-stack-x:2xs
  >
    <slot name="start" />
    <select
      ref="input"
      v-bind="$attrs"
      :value="value"
      :disabled="disabled || fieldState.disabled"
      @keydown.stop="handleKeydown"
      @change.stop="handleInput"
    >
      <option value="" :selected="!hasValue" disabled>
        {{ placeholder }}
      </option>
      <slot />
    </select>
    <SloTooltip v-if="toolTip" :text="toolTipMessage" position="bottom-right" :inset="true">
      <SloIcon name="CircleInfo" />
    </SloTooltip>
    <SloIcon name="CaretDownSolid" />
  </label>
</template>

<script>
import SloIcon from './SloIcon.vue';
import SloTooltip from './SloTooltip.vue';

export default {
  name: 'SloSelect',
  components: {
    SloIcon,
    SloTooltip
  },
  inheritAttrs: false,
  inject: {
    fieldState: {name: 'fieldState', default: {}}
  },
  props: {
    size: {type: String},
    disabled: {type: Boolean, default: false},
    variant: {type: String},
    placeholder: {type: String},
    value: {type: String|Number, default: ''},
    error: {type: [Boolean, String], default: false},
    toolTip: {type: Boolean},
    toolTipMessage: {type: String}
  },
  data() {
    return {
      hasValue: Boolean(this.value)
    };
  },
  methods: {
    setFocus() {
      this.$refs.input.focus();
    },
    handleKeydown(event) {
      if (event.key === 'Enter') return this.$emit('enter', event);
      if (event.key === 'Tab') return this.$emit('tab', event);
      if (event.key === 'Backspace') return this.$emit('backspace', event);
    },
    handleInput(event) {
      this.hasValue = Boolean(event.target.value);
      this.$emit('input', event.target.value);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../scss/system';

.slo-select {
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  font-weight: normal;
  height: auto;
  background: $color-white;
  border: 1px solid $color-gray-400;
  border-radius: 4px;
  overflow: hidden;

  select {
    @include text('base');
    position: relative;
    flex: 1 1 0%;
    width: 100%;
    border: none;
    box-shadow: none;
    margin: 0;
    padding: 12px 16px;
    height: 48px;
    font-weight: inherit;
    color: $color-gray-700;
    border-radius: 4px;
  }

  > .slo-icon:first-child {
    margin-left: 1em;
    margin-right: -8px;
  }

  > .slo-icon:last-child {
    position: absolute;
    top: 50%;
    right: 16px;
    z-index: 10;
    transform: translateY(-50%);
    pointer-events: none;
    color: $color-gray-400;
  }

  &.has-value > .slo-icon:last-child {
    color: $color-gray-700;
  }
  &.has-value select {
    color: $color-gray-900;
  }

  &:hover {
    border-color: $color-gray-600;

    & > .slo-icon:last-child {
      color: $color-gray-700;
    }
  }

  &:focus-within, &.focus-within {
    border-color: $color-blue-400;
    overflow: initial;
    outline: 1px solid transparent;
    box-shadow: 0 0 0 1px $color-blue-400;

    & > select {
      border-color: $color-blue-400 !important;
    }

    & > .slo-icon:last-child {
      color: $color-blue-400;
    }
  }

  &--error {
    border-color: $color-red-400;

    &:hover {
      border-color: $color-red-400;
    }

    & > select, &.has-value > select {
      color: $color-red-400;
      border-color: $color-red-400 !important;
    }

    & > .slo-icon:last-child, &.has-value > .slo-icon:last-child {
      color: $color-red-400;
    }

    &:focus-within, &.focus-within {
      border-color: $color-red-400;
      box-shadow: 0 0 0 1px $color-red-400;

      & > select {
        color: $color-red-400;
        border-color: $color-red-400 !important;
      }

      & > .slo-icon:last-child, &.has-value > .slo-icon:last-child {
        color: $color-red-400;
      }
    }
  }

  &--tooltip {
    overflow: unset;

    >.slo-icon:last-child {
      right: 36px;
    }
  }

  &.variant-inset {
    background: $color-gray-200;
    border-color: transparent;
    border-radius: 3px;

    input {
      background: inherit;
      border-radius: 3px;
    }
  }
}

</style>
